import * as React from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';
import { AccentFormCtrlNoViewState } from '../bound/AccentFormCtrl';
import { t, accentUtils, formHelper, format } from '../../services/HelperService';
import * as JobService from '../../services/JobService';
import {
    getWeekdayOccurrenceInMonth,
    getRecurrenceMonthlyPatternDescriptions,
} from '../../services/helpers/RecurrenceHelper';

export {
    getOrdinal,
    getRecurrenceMonthlyPatternDescriptions,
} from '../../services/helpers/RecurrenceHelper';

/** Recurrence end option ids (aligned with JobService.eventRecurrenceEndType). */
export const RECURRENCE_END = { NEVER: "never", ENDS_ON: "endsOn", ENDS_AFTER: "endsAfter" };

export const getRecurrenceEndType = (entity) => {
    if (!entity) return RECURRENCE_END.NEVER;
    if (entity.RecurrenceUntil != null) return RECURRENCE_END.ENDS_ON;
    if (entity.RecurrenceCount != null) return RECURRENCE_END.ENDS_AFTER;
    return RECURRENCE_END.NEVER;
};

/** Start/end/all-day validation for event schedule fields. */
export const validateEventScheduleDates = (entity) => {
    if (!entity) return null;
    const hasStart = !accentUtils.isNull(entity.Start);
    const hasEnd = !accentUtils.isNull(entity.End);
    if (entity.AllDay) {
        if (!hasStart) return { Msg: "Start time is required", Type: "Error" };
    } else {
        if (!hasStart || !hasEnd) return { Msg: "Start and end time are required", Type: "Error" };
        if (entity.Start >= entity.End) return { Msg: "End time cannot be before the start time", Type: "Error" };
    }
    return null;
};

/** Keep appointment duration when start changes (same behaviour as ActivityDlg). */
export const shiftEndWithStartChange = (entity, changeEvent, defaultDurationMinutes = 30) => {
    if (!entity) return;

    if (!accentUtils.isNull(entity.End) && !accentUtils.isNull(changeEvent?.originalValue)) {
        const diffMs = entity.End - changeEvent.originalValue;
        const diffMins = Math.round(diffMs / 1000 / 60);
        entity.End = new Date(entity.Start.getTime() + diffMins * 60000);
        return;
    }

    if (!accentUtils.isNull(entity.Start) && !accentUtils.isNull(entity.End) && entity.Start > entity.End) {
        entity.End = new Date(entity.Start.getTime() + defaultDurationMinutes * 60000);
    }
};

/** Default “ends on” date offset from series start when user picks “ends on” without a date yet. */
const DEFAULT_RECURRENCE_UNTIL_OFFSET_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_RECURRENCE_END_AFTER_COUNT = 10;

/** Apply never / ends on / ends after to an event entity (mutates). */
export const applyRecurrenceEndTypeToEntity = (entity, endTypeId, options = {}) => {
    if (!entity) return;
    const offsetMs = options.untilOffsetMs ?? DEFAULT_RECURRENCE_UNTIL_OFFSET_MS;
    const defaultCount = options.defaultOccurrenceCount ?? DEFAULT_RECURRENCE_END_AFTER_COUNT;
    if (endTypeId === RECURRENCE_END.NEVER) {
        entity.RecurrenceUntil = null;
        entity.RecurrenceCount = null;
    } else if (endTypeId === RECURRENCE_END.ENDS_ON) {
        entity.RecurrenceUntil = entity.RecurrenceUntil ?? new Date(entity.Start.getTime() + offsetMs);
        entity.RecurrenceCount = null;
    } else if (endTypeId === RECURRENCE_END.ENDS_AFTER) {
        entity.RecurrenceCount = entity.RecurrenceCount ?? defaultCount;
        entity.RecurrenceUntil = null;
    }
};

export const parseRecurrenceEndValue = (e) => {
    const v = e?.value;
    return (typeof v === "object" && v?.ID) ? v.ID : (typeof v === "string" ? v : e?.value ?? e);
};

/** Monthly/yearly pattern required on series schedule only (not on instance tab). */
export const validateEventRecurrenceMonthlyPattern = (value, entity, isInstanceTabSelected) => {
    if (!entity) return undefined;
    if (isInstanceTabSelected) return undefined;
    const unit = entity.RepeatEveryUnit;
    if (unit !== "repeatUnit_month" && unit !== "repeatUnit_year") return undefined;
    if (accentUtils.isNull(value)) return { Msg: "Required", Type: "Error" };
    return undefined;
};

/** Applies recurrence end type to the event entity and marks the dialog view model dirty. */
export function useRecurrenceEndTypeChange(viewModel) {
    return React.useCallback((value) => {
        if (!viewModel?.entity) return;
        applyRecurrenceEndTypeToEntity(viewModel.entity, value);
        viewModel.setDirty();
    }, [viewModel]);
}

/** Picklist source for monthly/yearly “RecurrenceByWeekday” pattern (descriptions follow start date). */
export const getRecurrenceMonthlyPatternPicklistSource = (startDate, repeatEveryUnit) => () => {
    const desc = getRecurrenceMonthlyPatternDescriptions(startDate, repeatEveryUnit);
    return [
        { ID: true, Description: desc.weekday },
        { ID: false, Description: desc.dayOfMonth },
    ];
};

/** Same wording as the monthly/year pattern dropdown; used in recurrence summary text. */
export const getMonthlyYearlyPatternClause = (startDate, repeatEveryUnit, recurrenceByWeekday) => {
    if (recurrenceByWeekday !== true && recurrenceByWeekday !== false) return "";
    const { dayOfMonth, weekday } = getRecurrenceMonthlyPatternDescriptions(startDate, repeatEveryUnit);
    return recurrenceByWeekday === true ? weekday : dayOfMonth;
};

/** iCalendar BYDAY codes: SU, MO, TU, WE, TH, FR, SA (matches Date.getDay() order) */
const BYDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const parseValue = (value) => {
    if (value == null || value === '') return [];
    return String(value).split(',')
        .map(part => part.trim().toUpperCase())
        .filter(code => BYDAY_CODES.includes(code));
};

const toStoredValue = (codes) => {
    if (!codes?.length) return null;
    const unique = [...new Set(codes)].filter(c => BYDAY_CODES.includes(c));
    if (!unique.length) return null;
    return unique.sort((a, b) => BYDAY_CODES.indexOf(a) - BYDAY_CODES.indexOf(b)).join(',');
};

const repeatUnitWord = (n, unitId) => {
    const plural = (n ?? 1) > 1;
    return t(`application_strings.views.calendar.${unitId}${plural ? "_plural" : ""}`);
};



const formatWeekdayList = (recurrenceWeekdays) => {
    const codes = parseValue(recurrenceWeekdays);
    if (!codes.length) return "";
    return codes.map((c) => t(`application_strings.views.calendar.day_${c}`)).join(", ");
};

const cloneDate = (value) => {
    if (accentUtils.isNull(value)) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const compareOccurrence = (a, b, allDay) => {
    if (!a || !b) return 0;
    if (allDay) {
        const aDate = new Date(a.getFullYear(), a.getMonth(), a.getDate());
        const bDate = new Date(b.getFullYear(), b.getMonth(), b.getDate());
        return aDate.getTime() - bDate.getTime();
    }
    return a.getTime() - b.getTime();
};

const atStartTime = (start, date) => {
    if (!start || !date) return null;
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        start.getHours(),
        start.getMinutes(),
        start.getSeconds(),
        start.getMilliseconds()
    );
};

const mondayOfWeek = (d) => {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = (7 + (date.getDay() - 1)) % 7;
    date.setDate(date.getDate() - diff);
    return date;
};

const parseRepeatInterval = (value) => {
    const n = Number(value);
    if (Number.isNaN(n) || n < 1) return 1;
    return Math.floor(n);
};

const getNthMonthly = (start, interval, n, recurrenceByWeekday) => {
    const target = new Date(start.getFullYear(), start.getMonth(), 1);
    target.setMonth(target.getMonth() + ((n - 1) * interval));
    const targetYear = target.getFullYear();
    const targetMonth = target.getMonth();
    if (recurrenceByWeekday === true) {
        const d = getWeekdayOccurrenceInMonth(start, targetYear, targetMonth);
        return atStartTime(start, d);
    }
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const day = Math.min(start.getDate(), lastDay);
    return atStartTime(start, new Date(targetYear, targetMonth, day));
};

const getNthYearly = (start, interval, n, recurrenceByWeekday) => {
    const targetYear = start.getFullYear() + ((n - 1) * interval);
    const targetMonth = start.getMonth();
    if (recurrenceByWeekday === true) {
        const d = getWeekdayOccurrenceInMonth(start, targetYear, targetMonth);
        return atStartTime(start, d);
    }
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const day = Math.min(start.getDate(), lastDay);
    return atStartTime(start, new Date(targetYear, targetMonth, day));
};

const matchesWeekly = (start, recurrenceWeekdays, interval, candidate) => {
    const configuredDays = parseValue(recurrenceWeekdays);
    const startDayCode = BYDAY_CODES[start.getDay()];
    const days = configuredDays.length > 0 ? configuredDays : [startDayCode];
    const candidateCode = BYDAY_CODES[candidate.getDay()];
    if (!days.includes(candidateCode)) return false;

    const anchorMonday = mondayOfWeek(start);
    const candidateMonday = mondayOfWeek(candidate);
    const weeks = Math.floor((candidateMonday.getTime() - anchorMonday.getTime()) / (7 * 24 * 60 * 60 * 1000));
    if (weeks < 0) return false;
    return weeks % interval === 0;
};

const nextWeeklyAfter = (start, recurrenceWeekdays, interval, current) => {
    for (let i = 1; i <= 800; i++) {
        const cand = new Date(current);
        cand.setDate(cand.getDate() + i);
        if (matchesWeekly(start, recurrenceWeekdays, interval, cand)) {
            return cand;
        }
    }
    return null;
};

const getNthOccurrenceStart = (details, n) => {
    const start = cloneDate(details?.Start);
    if (!start || n < 1) return null;
    const interval = parseRepeatInterval(details?.RepeatEveryNumber);
    const unit = details?.RepeatEveryUnit;

    if (unit === "repeatUnit_day") {
        const d = new Date(start);
        d.setDate(d.getDate() + ((n - 1) * interval));
        return d;
    }
    if (unit === "repeatUnit_week") {
        let cur = new Date(start);
        if (n === 1) return cur;
        for (let i = 2; i <= n; i++) {
            const nx = nextWeeklyAfter(start, details?.RecurrenceWeekdays, interval, cur);
            if (!nx) return null;
            cur = nx;
        }
        return cur;
    }
    if (unit === "repeatUnit_month") {
        return getNthMonthly(start, interval, n, details?.RecurrenceByWeekday);
    }
    if (unit === "repeatUnit_year") {
        return getNthYearly(start, interval, n, details?.RecurrenceByWeekday);
    }
    return null;
};

export const DayOfWeekButtons = ({ value, onChange, className, repeatEveryUnit, startDate, readOnly }) => {
    const disabled = repeatEveryUnit === "repeatUnit_day";

    React.useEffect(() => {
        const existing = parseValue(value);
        if (existing.length > 0) return;

        const isDayUnit = repeatEveryUnit === 'repeatUnit_day';
        const startDayCode = startDate ? BYDAY_CODES[new Date(startDate).getDay()] : null;

        if (isDayUnit) {
            onChange?.(toStoredValue([...BYDAY_CODES]));
        } else if (startDate != null) {
            onChange?.(toStoredValue([startDayCode]));
        }
    }, [onChange, repeatEveryUnit, startDate, value]);

    const toggleDay = React.useCallback((code) => {
        if (disabled) return;

        const days = parseValue(value);
        const startDayCode = startDate ? BYDAY_CODES[new Date(startDate).getDay()] : null;

        const hasDay = days.includes(code);
        const newDays = hasDay ? days.filter(d => d !== code) : [...days, code];
        const withStart = startDayCode && !newDays.includes(startDayCode) ? [...newDays, startDayCode] : newDays;

        onChange?.(toStoredValue(withStart));
    }, [onChange, disabled, value, startDate]);

    const days = parseValue(value);

    return (
        <ButtonGroup className={`d-flex w-100 flex-wrap ${className || ''}`} disabled={readOnly} >
            {BYDAY_CODES.map((code) => {
                const isSelected = days.includes(code);
                return (
                    <Button
                        key={code}
                        size="sm"
                        color={isSelected ? 'primary' : 'secondary'}
                        outline={!isSelected}
                        disabled={readOnly || disabled}
                        onClick={() => toggleDay(code)}
                        className="d-flex align-items-center justify-content-center flex-grow-1"
                        style={{ minWidth: '2.5rem', height: '2.5rem', flex: '1 1 0' }}
                        
                    >
                        {t(`application_strings.views.calendar.day_${code}`)}
                    </Button>
                );
            })}
        </ButtonGroup>
    );
};



/** Schedule + recurrence UI and bound fields. Owns form models and recurrence handlers (tabs stay in EventDlg). */
export const EventScheduleDetails = ({ scheduleMode, viewModel, readOnly, isInstanceTabSelected }) => {
    const entity = viewModel?.entity;

    const setDirty = React.useCallback(() => viewModel.setDirty(), [viewModel]);
    const onRecurrenceEndTypeChange = useRecurrenceEndTypeChange(viewModel);

    const validateDates = React.useCallback(() => validateEventScheduleDates(entity), [entity]);

    const recurrenceRequired = viewModel.isNew || !isInstanceTabSelected;

    const validateRepeatEveryIfRequired = React.useCallback((value) => {
        if (!recurrenceRequired) return null;
        return formHelper.getRequiredValidation(value);
    }, [recurrenceRequired]);

    const validateRecurrenceMonthlyPattern = React.useCallback((value) => (
        validateEventRecurrenceMonthlyPattern(value, entity, isInstanceTabSelected)
    ), [entity, isInstanceTabSelected]);

    const onRecurrenceWeekdaysChange = React.useCallback((v) => {
        if (!entity) return;
        entity.RecurrenceWeekdays = v;
        setDirty();
    }, [entity, setDirty]);

    const onStartChange = React.useCallback((e) => {
        shiftEndWithStartChange(entity, e);
        setDirty();
    }, [entity, setDirty]);

    if (!entity) return null;

    entity._recurrenceEndType = getRecurrenceEndType(entity);

    const start = formHelper.getFieldCtrlModel(entity, "Start", "datetime", validateDates, { label: "application_strings.entities.activity.Start", readOnly: readOnly || entity.AllDay, onChange: onStartChange });
    const end = formHelper.getFieldCtrlModel(entity, "End", "datetime", validateDates, { label: "application_strings.entities.activity.End", readOnly: readOnly || entity.AllDay });
    const allDay = formHelper.getFieldCtrlModel(entity, "AllDay", "check", false, { label: "application_strings.entities.activity.AllDay", readOnly: readOnly, onChange: setDirty });

    const getRepeatUnitSource = () => {
        const base = JobService.eventRecurrenceUnit.getPicklistSource();
        const plural = (entity.RepeatEveryNumber ?? 1) > 1;
        const suffix = plural ? "_plural" : "";
        return base.map((item) => ({ ...item, Description: t(`application_strings.views.calendar.${item.ID}${suffix}`) }));
    };

    const repeatEveryNumber = formHelper.getFieldCtrlModel(entity, "RepeatEveryNumber", "number", validateRepeatEveryIfRequired, { readOnly: readOnly, min: 1, placeHolder: "1", onChange: setDirty });
    const repeatEveryUnit = formHelper.getFieldCtrlModel(entity, "RepeatEveryUnit", "select", validateRepeatEveryIfRequired, { readOnly: readOnly, source: getRepeatUnitSource, onChange: setDirty });
    const recurrenceMonthlyPattern = formHelper.getFieldCtrlModel(entity, "RecurrenceByWeekday", "select", validateRecurrenceMonthlyPattern, { readOnly: readOnly, noLabel: true, source: getRecurrenceMonthlyPatternPicklistSource(entity.Start, entity.RepeatEveryUnit), onChange: setDirty });
    const recurrenceEndTypeModel = formHelper.getFieldCtrlModel(entity, "_recurrenceEndType", "select", false, { readOnly: readOnly, noLabel: true, source: JobService.eventRecurrenceEndType.getPicklistSource(), onChange: (e) => { const id = parseRecurrenceEndValue(e); if (Object.values(RECURRENCE_END).includes(id)) onRecurrenceEndTypeChange(id); } });
    const recurrenceUntil = formHelper.getFieldCtrlModel(entity, "RecurrenceUntil", "datetime", false, { readOnly: readOnly, noLabel: true, onChange: setDirty });
    const recurrenceCount = formHelper.getFieldCtrlModel(entity, "RecurrenceCount", "number", false, { readOnly: readOnly, min: 1, placeHolder: "10", noLabel: true, onChange: setDirty });

    if (scheduleMode === "instance") {
        return (
            <Row>
                <Col md={6} xs={12}>
                    <AccentFormCtrlNoViewState key={`start_occ_${entity.AllDay}`} model={start} />
                </Col>
                <Col md={6} xs={12} className="pt-2 pt-md-0">
                    <AccentFormCtrlNoViewState key={`end_occ_${entity.AllDay}`} model={end} />
                </Col>
            </Row>
        );
    }

    const recurrenceEndType = getRecurrenceEndType(entity);

    return (
        <>
            <Row>
                <Col md={6} xs={12}>
                    <AccentFormCtrlNoViewState key={`start_${entity.AllDay}`} model={start} />
                </Col>
                <Col md={6} xs={12} className="d-flex flex-column gap-2 pt-2 pt-md-0">
                    <div className="d-flex align-items-center gap-2">
                        <div className="flex-grow-1 min-w-0">
                            <AccentFormCtrlNoViewState key={`end_${entity.AllDay}`} model={end} />
                        </div>
                        <div className="flex-shrink-0" style={{ minWidth: 90 }}>
                            <AccentFormCtrlNoViewState model={allDay} />
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className="align-items-center pt-3">
                <Col xs={12} md={6} className="d-flex align-items-center gap-2 pt-2 pt-md-0">
                    <span className="flex-shrink-0">{t("application_strings.views.calendar.repeatEvery")}</span>
                    <div className="flex-grow-1 min-w-0" style={{ maxWidth: 80 }}>
                        <AccentFormCtrlNoViewState noLabel model={repeatEveryNumber} />
                    </div>
                    <div className="flex-grow-1 min-w-0">
                        <AccentFormCtrlNoViewState key={`repeatUnit_${entity.RepeatEveryNumber ?? 1}`} noLabel model={repeatEveryUnit} />
                    </div>
                </Col>
                {entity.RepeatEveryUnit === 'repeatUnit_week' && (
                    <Col xs={12} md={6} className="pt-2 pt-md-0" style={{ paddingTop: 18 }}>
                        <DayOfWeekButtons
                            readOnly={readOnly}
                            value={entity.RecurrenceWeekdays}
                            onChange={onRecurrenceWeekdaysChange}
                            repeatEveryUnit={entity.RepeatEveryUnit}
                            startDate={entity.Start}
                        />
                    </Col>
                )}
                {(entity.RepeatEveryUnit === 'repeatUnit_month' || entity.RepeatEveryUnit === 'repeatUnit_year') && (
                    <Col xs={12} md={6} className="pt-2 pt-md-0">
                        <AccentFormCtrlNoViewState
                            key={`recurrenceMonthlyPattern_${entity.RepeatEveryUnit}_${entity.Start instanceof Date && !Number.isNaN(entity.Start.getTime()) ? entity.Start.getTime() : ''}`}
                            noLabel
                            model={recurrenceMonthlyPattern}
                        />
                    </Col>
                )}
            </Row>
            <Row className="align-items-center" style={{ marginTop: 18 }}>
                <Col xs={12} md={6} className="pt-2 pt-md-0">
                    <AccentFormCtrlNoViewState key={`recurrenceEnd_${recurrenceEndType}`} noLabel model={recurrenceEndTypeModel} />
                </Col>
                {recurrenceEndType === RECURRENCE_END.ENDS_ON && (
                    <Col xs={12} md={6} className="pt-2 pt-md-0">
                        <AccentFormCtrlNoViewState noLabel model={recurrenceUntil} />
                    </Col>
                )}
                {recurrenceEndType === RECURRENCE_END.ENDS_AFTER && (
                    <Col xs={12} md={6} className="d-flex align-items-center gap-2 pt-2 pt-md-0">
                        <div style={{ minWidth: 70 }}>
                            <AccentFormCtrlNoViewState noLabel model={recurrenceCount} />
                        </div>
                        <span>{(entity.RecurrenceCount ?? 1) > 1 ? t("application_strings.views.calendar.instances") : t("application_strings.views.calendar.instance")}</span>
                    </Col>
                )}
            </Row>
        </>
    );
};


const occursEveryWithUnit = (repeatEveryNumber, unitId) => {
    const n = repeatEveryNumber ?? 1;
    const u = repeatUnitWord(n, unitId);
    const prefix = t("application_strings.views.calendar.eventOccurs_every");
    if (n === 1) return `${prefix} ${u}`;
    return `${prefix} ${n} ${u}`;
};

/** Second sentence for the recurrence summary when end is set (ends on / ends after). */
const recurrenceEndSummaryClause = (details) => {
    if (!details || details.scope === "instance") return "";
    const endType = details.recurrenceEndType ?? getRecurrenceEndType({
        RecurrenceUntil: details.RecurrenceUntil,
        RecurrenceCount: details.RecurrenceCount,
    });
    if (endType === RECURRENCE_END.NEVER) return "";

    if (endType === RECURRENCE_END.ENDS_ON) {
        const until = details.RecurrenceUntil;
        if (accentUtils.isNull(until)) return "";
        const dateStr = details.AllDay ? format.formatDate(until) : format.formatDateTime(until);
        if (!dateStr) return "";
        return t("application_strings.views.calendar.eventOccurs_untilDate", { date: dateStr });
    }

    if (endType === RECURRENCE_END.ENDS_AFTER) {
        const n = details.RecurrenceCount;
        if (accentUtils.isNull(n) || n < 1) return "";
        const unit = n > 1
            ? t("application_strings.views.calendar.instances")
            : t("application_strings.views.calendar.instance");
        const base = t("application_strings.views.calendar.eventOccurs_afterOccurrences", { n, unit });
        const lastSeries = getNthOccurrenceStart(details, Number(n));
        if (!accentUtils.isNull(lastSeries) && compareOccurrence(lastSeries, new Date(0), details.AllDay) > 0) {
            const lastStr = details.AllDay ? format.formatDate(lastSeries) : format.formatDateTime(lastSeries);
            if (lastStr) {
                return `${base}. ${t("application_strings.views.calendar.eventOccurs_lastSeriesDate", { date: lastStr })}`;
            }
        }
        return base;
    }

    return "";
};

const parseRRuleDate = (value) => {
    if (accentUtils.isEmpty(value)) return null;
    const raw = `${value}`.trim();

    const ymd = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (ymd) {
        const [, y, m, d] = ymd;
        return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
    }

    const ymdhms = raw.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z?$/);
    if (ymdhms) {
        const [, y, m, d, hh, mm, ss] = ymdhms;
        return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)));
    }

    const dt = new Date(raw);
    return Number.isNaN(dt.getTime()) ? null : dt;
};

const parseRRuleForSummary = (recurrenceRule) => {
    if (accentUtils.isEmpty(recurrenceRule)) return null;

    const map = {};
    `${recurrenceRule}`.split(";").forEach((part) => {
        const trimmed = part.trim();
        if (!trimmed) return;
        const idx = trimmed.indexOf("=");
        if (idx <= 0) return;
        const key = trimmed.slice(0, idx).toUpperCase();
        const value = trimmed.slice(idx + 1);
        map[key] = value;
    });

    const unitMap = {
        DAILY: "repeatUnit_day",
        WEEKLY: "repeatUnit_week",
        MONTHLY: "repeatUnit_month",
        YEARLY: "repeatUnit_year",
    };

    const repeatEveryUnit = unitMap[map.FREQ];
    if (accentUtils.isEmpty(repeatEveryUnit)) return null;

    const interval = Number(map.INTERVAL);
    const count = Number(map.COUNT);
    return {
        RepeatEveryUnit: repeatEveryUnit,
        RepeatEveryNumber: Number.isNaN(interval) || interval < 1 ? 1 : interval,
        RecurrenceWeekdays: map.BYDAY ?? null,
        RecurrenceByWeekday: (!accentUtils.isEmpty(map.BYDAY) && accentUtils.isEmpty(map.BYMONTHDAY)) ? true : null,
        RecurrenceUntil: parseRRuleDate(map.UNTIL),
        RecurrenceCount: Number.isNaN(count) || count < 1 ? null : count,
    };
};

const normalizeSummaryDetails = (details) => {
    if (!details) return null;
    const recurrenceRule = details.RecurrenceRule ?? details.recurrenceRule;
    const fromRule = parseRRuleForSummary(recurrenceRule);
    const start = details.Start ?? details.start ?? details.startDate;
    const until = details.RecurrenceUntil ?? details.recurrenceUntil;

    return {
        ...details,
        Start: accentUtils.isNull(start) ? null : new Date(start),
        RepeatEveryUnit: details.RepeatEveryUnit ?? details.repeatEveryUnit ?? fromRule?.RepeatEveryUnit,
        RepeatEveryNumber: details.RepeatEveryNumber ?? details.repeatEveryNumber ?? fromRule?.RepeatEveryNumber ?? 1,
        RecurrenceWeekdays: details.RecurrenceWeekdays ?? details.recurrenceWeekdays ?? fromRule?.RecurrenceWeekdays,
        RecurrenceByWeekday: details.RecurrenceByWeekday ?? details.recurrenceByWeekday ?? fromRule?.RecurrenceByWeekday,
        RecurrenceUntil: accentUtils.isNull(until) ? (fromRule?.RecurrenceUntil ?? null) : new Date(until),
        RecurrenceCount: details.RecurrenceCount ?? details.recurrenceCount ?? fromRule?.RecurrenceCount ?? null,
        AllDay: details.AllDay ?? details.allDay,
    };
};

export const EventSchedulingDataSummary = ({ schedulingDetails, compact }) => {
    if (!schedulingDetails) return null;
    const normalizedDetails = normalizeSummaryDetails(schedulingDetails);

    const isInstance =
        normalizedDetails?.isInstance === true
        || normalizedDetails?.IsInstance === true
        || `${normalizedDetails?.scope ?? ""}`.toLowerCase() === "instance"
        || `${normalizedDetails?.EventType ?? normalizedDetails?.eventType ?? ""}`.toLowerCase() === "instance";
    if (isInstance) {
        const anchor = normalizedDetails?.InstanceOriginalStart ?? normalizedDetails?.instanceOriginalStart;
        const text = anchor
            ? `Instance of recurring series (original: ${format.formatDateTime(anchor)})`
            : "Instance of recurring series";
        return <div className={compact ? "" : "text-muted small mt-2"}>{text}</div>;
    }

    const {
        Start,
        RepeatEveryUnit: repeatEveryUnit,
        RepeatEveryNumber: repeatEveryNumber = 1,
        RecurrenceWeekdays: recurrenceWeekdays,
        RecurrenceByWeekday: recurrenceByWeekday,
    } = normalizedDetails;

    let text;
    switch (repeatEveryUnit) {
        case "repeatUnit_day":
            text = repeatEveryNumber === 1
                ? t("application_strings.views.calendar.eventOccurs_daily")
                : occursEveryWithUnit(repeatEveryNumber, "repeatUnit_day");
            break;
        case "repeatUnit_week": {
            const days = formatWeekdayList(recurrenceWeekdays);
            const core = occursEveryWithUnit(repeatEveryNumber, "repeatUnit_week");
            text = days ? `${core}${t("application_strings.views.calendar.eventOccurs_onDays", { days })}` : core;
            break;
        }
        case "repeatUnit_month": {
            const interval = occursEveryWithUnit(repeatEveryNumber, "repeatUnit_month");
            const clause = getMonthlyYearlyPatternClause(Start, repeatEveryUnit, recurrenceByWeekday);
            text = clause ? `${interval}. ${clause}` : interval;
            break;
        }
        case "repeatUnit_year": {
            const interval = occursEveryWithUnit(repeatEveryNumber, "repeatUnit_year");
            const clause = getMonthlyYearlyPatternClause(Start, repeatEveryUnit, recurrenceByWeekday);
            text = clause ? `${interval}. ${clause}` : interval;
            break;
        }
        default:
            text = t("application_strings.views.calendar.eventOccurs_noPattern");
    }

    const endClause = recurrenceEndSummaryClause(normalizedDetails);
    const fullText = endClause ? `${text}. ${endClause}` : text;

    return <div className={compact ? "" : "text-muted small mt-2"}>{fullText}</div>;
};



